/*
 * Books' review rules (Oct 30, like QuickBooks' bank feed): what kind each account is, which bank lines are the two
 * sides of one transfer between our own accounts, what category a pair gets, and how sure Books is of a line's category
 * (sure, a guess, or worth a second look).
 */

/** What an account is. Checking, savings and cash hold money; a card, a line of credit and a loan are money owed. */
export const ACCOUNT_KINDS = ["checking", "savings", "card", "credit", "loan", "cash"] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];

export const ACCOUNT_KIND_WORDS: Record<AccountKind, string> = {
  checking: "Checking",
  savings: "Savings",
  card: "Credit card",
  credit: "Line of credit",
  loan: "Loan",
  cash: "Cash",
};

/** A card, line of credit or loan: its balance is what the business owes, a minus amount in Books. */
export const isOwed = (kind: string): boolean => kind === "card" || kind === "credit" || kind === "loan";

/** The kind for an account a bank sends through Plaid (its type and subtype). */
export function kindFromPlaid(type: string, subtype: string | null): AccountKind {
  const sub = (subtype ?? "").toLowerCase();
  if (type === "credit") return "card";
  if (type === "loan") return sub === "line of credit" ? "credit" : "loan";
  if (type === "depository" && ["savings", "money market", "cd", "hsa"].includes(sub)) return "savings";
  return "checking";
}

/** Plaid's balance (always plus, what is in it or what is owed) as Books keeps it: owed is minus. */
export const bankBalanceCents = (kind: string, current: number): number => (isOwed(kind) ? -current : current);

export interface PairSide {
  id: string;
  accountId: string;
  day: string;
  amountCents: number;
}

const DAY_MS = 86_400_000;
const daysApart = (a: string, b: string) => Math.abs(Date.parse(`${a}T12:00:00Z`) - Date.parse(`${b}T12:00:00Z`)) / DAY_MS;

/** Banks post the two sides of a transfer up to this many days apart. */
export const PAIR_DAYS = 5;

/**
 * The other side of a transfer: the same amount the other way, in another of our accounts, within PAIR_DAYS. Nearest day
 * first. Neither side may be paired already (the caller leaves those out).
 */
export function pairsFor<T extends PairSide>(row: PairSide, others: readonly T[]): T[] {
  return others
    .filter(
      (o) =>
        o.id !== row.id &&
        o.accountId !== row.accountId &&
        o.amountCents === -row.amountCents &&
        daysApart(o.day, row.day) <= PAIR_DAYS,
    )
    .sort((a, b) => daysApart(a.day, row.day) - daysApart(b.day, row.day));
}

/**
 * The category code both sides of a pair get. Paying a card or line of credit is a card payment, paying a loan is a loan
 * payment, anything else between business accounts is a transfer. Money to or from the owner's own account is a draw or
 * money put in, and only the business side counts (the personal side is left out of the books).
 */
export function pairCode(
  self: { kind: string; personal: boolean; amountCents: number },
  other: { kind: string; personal: boolean },
): "transfer" | "cardpay" | "loanpay" | "draw" | "putin" {
  if (other.personal && !self.personal) return self.amountCents < 0 ? "draw" : "putin";
  if (self.personal && !other.personal) return self.amountCents > 0 ? "draw" : "putin";
  const kinds = [self.kind, other.kind];
  if (kinds.includes("loan")) return "loanpay";
  if (kinds.includes("card") || kinds.includes("credit")) return "cardpay";
  return "transfer";
}

export type Sureness = "sure" | "guess" | "check";

/** Words that say a line is money moving between accounts, not a sale or a cost. */
const MOVING = /\b(transfer|xfer|online pmt|autopay|payment thank you|epay|to sav|from sav|to chk|from chk)\b/i;

/**
 * How sure Books is of a line's category. sure: a person picked it, or it is paired, matched or a split payout. guess: a
 * rule or last time's category. check: something looks off (no category, money in on a cost, money out on income, or
 * transfer words on income or a cost).
 */
export function surenessOf(t: {
  amountCents: number;
  description: string;
  categoryType: string | null;
  auto: boolean;
  split: boolean;
  paired: boolean;
  matched: boolean;
  reviewed: boolean;
}): { level: Sureness; why: string } {
  if (t.paired) return { level: "sure", why: "Paired with the other side of the transfer" };
  if (t.matched) return { level: "sure", why: "Matched to payments in the Steward" };
  if (t.split) return { level: "sure", why: "Split from the payout's own record" };
  if (!t.categoryType) return { level: "check", why: "Needs a category" };
  if (t.amountCents > 0 && t.categoryType === "expense")
    return { level: "check", why: "Money came in but the category is a cost. Is it a refund?" };
  if (t.amountCents < 0 && t.categoryType === "income")
    return { level: "check", why: "Money went out but the category is income" };
  if ((t.categoryType === "income" || t.categoryType === "expense") && MOVING.test(t.description))
    return { level: "check", why: "The bank's words look like a transfer. Pair it if it went to another of your accounts" };
  if (t.reviewed && !t.auto) return { level: "sure", why: "You picked it" };
  return { level: "guess", why: t.auto ? "Picked by a rule" : "A guess. Check it and post" };
}
